import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UserPlus, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const { t, lang } = useLanguage();
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const displayFont = lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)';

  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, form.phone);
      navigate('/portal');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-gradient-to-br from-[#0B2F44] to-[#071E2C] flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-[#0B2F44] rounded-xl flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-7 h-7 text-[#C9A84C]" />
            </div>
            <h1 className="text-2xl font-bold text-[#0B2F44]" style={{ fontFamily: displayFont }}>
              {t('nav', 'register')}
            </h1>
            <p className="text-sm text-[#4A6572] mt-1">
              {lang === 'ar' ? 'أنشئ حسابك للوصول إلى بوابة العميل' : 'Create your account to access the portal'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm text-[#4A6572] block mb-1.5">{t('contact', 'name')} *</label>
              <Input
                data-testid="register-name-input"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]"
                required
              />
            </div>
            <div>
              <label className="text-sm text-[#4A6572] block mb-1.5">{t('contact', 'email')} *</label>
              <Input
                data-testid="register-email-input"
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]"
                required
              />
            </div>
            <div>
              <label className="text-sm text-[#4A6572] block mb-1.5">{lang === 'ar' ? 'كلمة المرور' : 'Password'} *</label>
              <div className="relative">
                <Input
                  data-testid="register-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  className="h-[52px] border-[#D0D8DC] focus:border-[#A68966] pe-12"
                  required
                  minLength={6}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#4A6572]">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-[#4A6572] block mb-1.5">{t('contact', 'phone')}</label>
              <Input
                data-testid="register-phone-input"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]"
              />
            </div>

            {error && <p data-testid="register-error" className="text-[#C0392B] text-sm bg-[#C0392B]/5 p-3 rounded-lg">{error}</p>}

            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold py-6"
            >
              {loading ? (lang === 'ar' ? 'جاري التسجيل...' : 'Creating account...') : t('nav', 'register')}
            </Button>
          </form>

          <p className="text-center text-sm text-[#4A6572] mt-6">
            {lang === 'ar' ? 'لديك حساب بالفعل؟' : 'Already have an account?'}{' '}
            <Link to="/login" className="text-[#A68966] font-semibold hover:underline">{t('nav', 'login')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
